import { CircleGeometry, DoubleSide, Mesh, MeshStandardMaterial, Quaternion, Vector3 } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugDiscConfig } from '../types';

export interface AddDiscParams {
	config: DebugDiscConfig;
}

/**
 * Add a debug disc / circular plane (registered as a sphere for tracking/cleanup)
 */
export const addDiscCfn = (
	self: DebugManager,
	{ config }: AddDiscParams
): Mesh | null => {
	const { id, position, radius = 2, color = 0xffff00, label, normal } = config;

	if (!radius || radius <= 0 || !Number.isFinite(radius)) {
		return null;
	}

	if (!Number.isFinite(position.x) || !Number.isFinite(position.y) || !Number.isFinite(position.z)) {
		return null;
	}

	// Remove existing mesh with same ID
	if (self.hasSphere(id)) {
		self.removeSphere(id);
	}

	const geometry = new CircleGeometry(radius, 32);
	const material = new MeshStandardMaterial({
		color,
		side: DoubleSide,
		transparent: true,
		opacity: 0.6
	});
	const disc = new Mesh(geometry, material);
	disc.position.copy(position);

	// Orient disc perpendicular to the provided normal
	// CircleGeometry faces +Z by default, rotate to face the normal direction
	const defaultNormal = new Vector3(0, 0, 1);
	const normalizedNormal = normal.clone().normalize();
	const quaternion = new Quaternion().setFromUnitVectors(defaultNormal, normalizedNormal);
	disc.quaternion.copy(quaternion);

	disc.name = `debug_disc_${id}`;
	disc.visible = self.visible;
	disc.userData.isInterface = true;
	disc.userData.type = 'debugVisualization';

	self.scene.add(disc);
	self.registerSphere(id, disc);

	if (label) {
		self.registerSphereLabel(id, label);
		self.createTooltip(id);
	}

	if (self.visible && !self.hoverEnabled) {
		self.enableHoverDetection();
	}

	return disc;
};
